import React from 'react'

import type { TChatSummaryDto } from '@homepal/api-contract'

import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Icon from 'components_new/atoms/Icon'
import Text from 'components_new/atoms/Text'

import { Highlighted } from 'components_new/organisms/StandardSearch/utils'

import { ChatLabelKey, chatWhen, rowCountLabel } from './historyLabels'

interface ChatHistoryRowProps {
  chat: TChatSummaryDto
  onSelect: () => void
  search: string
}

const ChatHistoryRow = (props: ChatHistoryRowProps) => {
  const { chat, onSelect, search } = props

  const { t } = useTranslation()
  const translate = (key: ChatLabelKey, values?: Record<string, string>) =>
    t('chat', key, false, values)

  const subtitle = [
    chatWhen(chat.created_at),
    chat.same_metric_view ? null : translate('history_other_view'),
    rowCountLabel(chat.row_count, translate)
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <Box
      onClick={onSelect}
      sx={{
        alignItems: 'flex-start',
        cursor: 'pointer',
        display: 'flex',
        gap: 1.5,
        px: 2,
        py: 1.25,
        '&:hover': { bgcolor: 'action.hover' }
      }}
    >
      <Box
        sx={{
          alignItems: 'center',
          bgcolor: 'action.hover',
          borderRadius: 1,
          display: 'flex',
          flex: '0 0 auto',
          height: 32,
          justifyContent: 'center',
          mt: 0.25,
          width: 32
        }}
      >
        <Icon color="disabled" fontSize="small" name="Chat" />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Text color="text.primary" variant="subtitle2">
          <Highlighted highlight={search} text={chat.title} />
        </Text>
        <Text
          color="text.secondary"
          variant="caption"
          sx={{ display: 'block' }}
        >
          {subtitle}
        </Text>
        {chat.read_only ? (
          <Text
            color="text.disabled"
            variant="caption"
            sx={{
              alignItems: 'center',
              display: 'flex',
              gap: 0.5,
              mt: 0.25
            }}
          >
            <Icon fontSize="inherit" name="RefreshOutlined" />
            {t('chat', 'history_stale')}
          </Text>
        ) : null}
      </Box>

      <Icon
        color="disabled"
        fontSize="small"
        name="ArrowForward"
        sx={{ flex: '0 0 auto', mt: 0.75 }}
      />
    </Box>
  )
}

export default ChatHistoryRow
